/**
 * Import de tiers depuis un fichier CSV.
 *
 * Deux temps imposés : on simule, on lit le rapport, on valide. La simulation
 * n'écrit rien — c'est ce qui permet de corriger le fichier avant d'engager
 * quoi que ce soit dans la base.
 */
import { useState } from 'react';

import { ApiError, api, type ImportReport, type RowOutcome } from '../api/client';
import { ZoneDepot } from '../composants/ZoneDepot';
import { accorde } from '../domaine/format';
import { libelleRole } from '../domaine/roles';

const OUTCOME: Record<RowOutcome, { texte: string; classe: string }> = {
  READY: { texte: 'Prêt', classe: 'actif' },
  WARNING: { texte: 'À vérifier', classe: 'attente' },
  DUPLICATE: { texte: 'Doublon', classe: 'attente' },
  INCOMPLETE: { texte: 'Incomplet', classe: 'suspendu' },
  ERROR: { texte: 'Erreur', classe: 'revoque' },
};

export function ImportPage() {
  const [fichier, setFichier] = useState<{ nom: string; contenu: string } | null>(null);
  const [rapport, setRapport] = useState<ImportReport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // La zone de dépôt affiche le nom du fichier gardé par `fichier` et remet son champ à zéro d'elle-même :
  // oublier le fichier suffit.
  const oublierFichier = () => setFichier(null);

  const choisir = (f: File | null) => {
    setErreur(null);
    setSucces(null);
    setRapport(null);
    if (!f) {
      setFichier(null);
      return;
    }
    const lecteur = new FileReader();
    lecteur.onload = () => setFichier({ nom: f.name, contenu: String(lecteur.result ?? '') });
    lecteur.onerror = () => setErreur('Lecture du fichier impossible.');
    // UTF-8 imposé : c'est l'encodage interne du produit. Un fichier en
    // Windows-1252 se verra aux accents cassés dans l'aperçu, avant écriture.
    lecteur.readAsText(f, 'utf-8');
  };

  const simuler = async () => {
    if (!fichier) return;
    setEnCours(true);
    setErreur(null);
    try {
      setRapport(await api.simulateImport(fichier.nom, fichier.contenu));
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Simulation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const valider = async () => {
    if (!rapport) return;
    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await api.commitImport(rapport.jobId);
      // « tiers » est invariable : seul le participe s'accorde.
      setSucces(
        `${resultat.created} tiers ${accorde(resultat.created, 'créé')}.` +
          ' Retrouvez-les dans la liste des tiers.',
      );
      setRapport(null);
      oublierFichier();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Validation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const exploitables = rapport ? rapport.totals.ready + rapport.totals.warning : 0;

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Importer des tiers</h1>
          <p>
            Déposez un fichier CSV. Rien n’est écrit tant que vous n’avez pas validé le rapport.
          </p>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {succes && <div className="message succes">{succes}</div>}

      <div className="carte">
        <h2>1. Choisir le fichier</h2>
        <div className="carte-corps">
          <ZoneDepot
            accept=".csv,text/csv"
            libelle="Glissez le fichier CSV ici, ou"
            nomChoisi={fichier?.nom ?? null}
            onFichiers={(fichiers) => choisir(fichiers[0] ?? null)}
          />
          <p className="aide">
            Séparateur point-virgule ou virgule, encodage UTF-8. Colonnes reconnues :
            {' '}
            <em>Raison sociale</em> (obligatoire), <em>Nom commercial</em>, <em>SIRET</em>,{' '}
            <em>TVA</em>, <em>Email</em>, <em>Téléphone</em>, <em>Adresse</em>,{' '}
            <em>Code postal</em>, <em>Ville</em>, <em>Rôles</em>.
          </p>
          <p className="aide">
            Un tiers peut cumuler plusieurs rôles, séparés par des virgules :
            {' '}
            <code>fournisseur,marque</code>. Valeurs acceptées : client, fournisseur,
            sous-traitant, organisme de contrôle, fabricant, marque.
          </p>

          {fichier && (
            <div style={{ marginTop: 14 }}>
              <button onClick={() => void simuler()} disabled={enCours}>
                {enCours ? 'Analyse…' : `Analyser « ${fichier.nom} »`}
              </button>
            </div>
          )}
        </div>
      </div>

      {rapport && (
        <>
          <div className="carte">
            <h2>2. Rapport de simulation</h2>
            <div className="carte-corps">
              <p>
                <strong>
                  {rapport.totals.total} {accorde(rapport.totals.total, 'ligne')}{' '}
                  {accorde(rapport.totals.total, 'analysée')}
                </strong>{' '}
                — {rapport.totals.ready} {accorde(rapport.totals.ready, 'prête')},{' '}
                {rapport.totals.warning} à vérifier, {rapport.totals.duplicate}{' '}
                {accorde(rapport.totals.duplicate, 'doublon')}, {rapport.totals.error} en erreur.
              </p>
              <div className="message info">
                Aucune donnée n’a encore été écrite. La validation créera{' '}
                <strong>{exploitables} tiers</strong> ; les doublons et les lignes en erreur
                seront ignorés, pas bloquants.
              </div>
              <button onClick={() => void valider()} disabled={enCours || exploitables === 0}>
                {enCours ? 'Import en cours…' : `Valider et créer ${exploitables} tiers`}
              </button>
            </div>
          </div>

          <div className="carte">
            <h2>Détail ligne par ligne</h2>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Ligne</th>
                  <th style={{ width: 110 }}>État</th>
                  <th>Tiers</th>
                  <th>Observations</th>
                </tr>
              </thead>
              <tbody>
                {rapport.rows.map((r) => {
                  const etat = OUTCOME[r.outcome];
                  const roles = ((r.preview?.['roles'] as string[] | undefined) ?? []).map(
                    libelleRole,
                  );
                  return (
                    <tr key={r.lineNumber}>
                      <td>{r.lineNumber}</td>
                      <td>
                        <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                      </td>
                      <td>
                        <strong>{(r.preview?.['legalName'] as string) ?? '—'}</strong>
                        {roles.length > 0 && <div className="aide">{roles.join(', ')}</div>}
                      </td>
                      <td>
                        {r.messages.length === 0 ? (
                          <span className="aide">—</span>
                        ) : (
                          r.messages.map((m, i) => (
                            <div key={i} className="aide">
                              {m}
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
